import { useTranslation } from 'react-i18next';
import { haptic } from '../../lib/haptic';
import { useAuth } from '../../lib/auth';

export function HelpRoute() {
  const { t } = useTranslation('help');
  // Helpline comes from the operator's Company Contacts (org.supportPhone),
  // surfaced via /driver-auth/me → useAuth — so it tracks the connect.fleetup.in
  // Contacts page. Falls back to the i18n default only when the org hasn't set one.
  const orgSupportPhone = useAuth((s) => s.driver?.ownerOrgSupportPhone);
  const helpline = orgSupportPhone || t('dispatchHelpline');
  const buildId = import.meta.env.MODE === 'development' ? 'dev' : 'prod';

  return (
    <div className="px-5 pt-2 pb-10">
      <h1 className="text-2xl font-extrabold text-[var(--color-text)] mb-4">{t('help')}</h1>

      {/* Helpline hero — solid green gradient, tappable tel: link */}
      <a
        href={`tel:${helpline}`}
        aria-label={t('callHelpline')}
        onClick={() => haptic.success()}
        className="flex items-center gap-3.5 no-underline"
        style={{
          padding: '18px',
          background: 'linear-gradient(135deg, var(--color-primary-500,#2E9E65) 0%, var(--green-900,#0F4A2C) 100%)',
          color: 'white',
          borderRadius: 'var(--radius-card)',
          boxShadow: '0 14px 30px rgba(46, 158, 101, 0.28), 0 2px 6px rgba(15, 59, 38, 0.10)',
        }}
      >
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: 54,
            height: 54,
            borderRadius: 16,
            background: 'rgba(255, 255, 255, 0.18)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
          }}
        >
          <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ color: 'white' }}>
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="serif-accent"
            style={{
              fontSize: 16,
              color: 'rgba(230, 241, 234, 0.95)',
            }}
          >
            {t('helpline.anchor')}
          </div>
          <div
            className="font-extrabold mt-1"
            style={{
              fontSize: 22,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {helpline}
          </div>
          <div className="mt-1" style={{ fontSize: 12, opacity: 0.85 }}>
            {t('helpline.subhead')}
          </div>
        </div>
        <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ color: 'white', opacity: 0.85, flexShrink: 0 }}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </a>

      {/* FAQ section */}
      <h2 className="text-[11px] font-bold uppercase mt-6 mb-3" style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}>
        {t('faqTitle')}
      </h2>
      <div
        className="overflow-hidden"
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-card)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        {((t('faq.items', { returnObjects: true }) as unknown as Array<{ q: string; a: string }>) || []).map((item, i, arr) => (
          <details
            key={i}
            className="group"
            style={{
              borderBottom: i < arr.length - 1 ? '1px solid var(--color-border)' : 'none',
            }}
          >
            <summary
              onClick={() => haptic.tap()}
              className="flex items-center justify-between gap-3 cursor-pointer select-none"
              style={{
                listStyle: 'none',
                padding: '16px 18px',
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--color-text)',
              }}
            >
              <span className="flex-1 min-w-0">{item.q}</span>
              <svg
                width={16} height={16} viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth={2}
                strokeLinecap="round" strokeLinejoin="round" aria-hidden
                className="transition-transform duration-200 group-open:rotate-180"
                style={{ color: 'var(--color-text-muted,#6B7480)', flexShrink: 0 }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </summary>
            <p
              style={{
                padding: '0 18px 16px',
                fontSize: 13,
                color: 'var(--color-text-secondary)',
                lineHeight: 1.6,
              }}
            >
              {item.a}
            </p>
          </details>
        ))}
      </div>

      <p
        className="text-center mt-8"
        style={{ fontSize: 11, color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.04em' }}
      >
        {t('version')} {t('buildId', { id: buildId })}
      </p>
    </div>
  );
}
